/**
 * Velocity Tracking — monitors event throughput rates globally
 * and per entity to detect information surges.
 *
 * - 1-hour sliding windows
 * - Velocity levels: normal (<3/hr), elevated (3-5/hr), spike (>=6/hr)
 * - Trend detection: rising (1.5x previous window), stable, falling
 * - Tracks both global throughput and per-entity rates
 */

import type { NormalizedEvent } from '@/sources/base-source';
import type { VelocityMetrics, VelocityLevel, VelocityTrend } from './types';
import { getEventTimestamp } from './types';

const ONE_HOUR_MS = 60 * 60 * 1000;
const RISING_THRESHOLD = 1.5; // 1.5x previous window = rising

// ---------------------------------------------------------------------------
// Internal state — persists across tick() calls
// ---------------------------------------------------------------------------

/** Sliding window: stores event counts per entity per hour-bucket. */
interface WindowBucket {
  timestamp: number; // Start of the hour
  globalCount: number;
  entityCounts: Map<string, number>;
}

/** Previous windows for trend detection. */
const _windows: WindowBucket[] = [];
const MAX_WINDOWS = 24; // Keep 24 hours of history

/** Floor a timestamp to the start of its hour. */
function floorToHour(ms: number): number {
  return Math.floor(ms / ONE_HOUR_MS) * ONE_HOUR_MS;
}

/** Classify a rate into a velocity level. */
function classifyLevel(rate: number): VelocityLevel {
  if (rate >= 6) return 'spike';
  if (rate >= 3) return 'elevated';
  return 'normal';
}

/** Compare current rate to previous window rate for trend. */
function classifyTrend(currentRate: number, previousRate: number): VelocityTrend {
  if (previousRate === 0) {
    return currentRate > 0 ? 'rising' : 'stable';
  }
  const ratio = currentRate / previousRate;
  if (ratio >= RISING_THRESHOLD) return 'rising';
  if (ratio <= 1 / RISING_THRESHOLD) return 'falling';
  return 'stable';
}

// ---------------------------------------------------------------------------
// Main function
// ---------------------------------------------------------------------------

/**
 * Calculate velocity metrics for the current event set.
 *
 * Counts events in the current 1-hour window, compares to previous window
 * for trend detection, and tracks per-entity rates.
 */
export function calculateVelocity(events: NormalizedEvent[]): VelocityMetrics {
  const now = Date.now();
  const currentWindowStart = floorToHour(now);
  const previousWindowStart = currentWindowStart - ONE_HOUR_MS;

  // Count events in current window
  let globalCount = 0;
  const entityCounts = new Map<string, number>();

  for (const event of events) {
    const ts = getEventTimestamp(event);
    if (ts < currentWindowStart) continue;

    globalCount++;

    // Count by entity (use event source + type as entity key)
    const entityKey = `${event.source}:${event.type}`;
    entityCounts.set(entityKey, (entityCounts.get(entityKey) ?? 0) + 1);

    // Also count by entity references if present
    if (event.entities) {
      for (const ent of event.entities) {
        entityCounts.set(ent.entityId, (entityCounts.get(ent.entityId) ?? 0) + 1);
      }
    }
  }

  // Store current window (trim old windows)
  const existingIdx = _windows.findIndex(w => w.timestamp === currentWindowStart);
  if (existingIdx >= 0) {
    _windows[existingIdx] = { timestamp: currentWindowStart, globalCount, entityCounts };
  } else {
    _windows.push({ timestamp: currentWindowStart, globalCount, entityCounts });
  }
  while (_windows.length > MAX_WINDOWS) {
    _windows.shift();
  }

  // Find previous window for trend comparison
  const previousWindow = _windows.find(w => w.timestamp === previousWindowStart);
  const previousGlobalCount = previousWindow?.globalCount ?? 0;

  // Calculate elapsed fraction of the current hour for rate normalization
  const elapsedMs = now - currentWindowStart;
  const hourFraction = Math.max(0.1, elapsedMs / ONE_HOUR_MS); // Min 6 min to avoid huge rates

  // Normalize to per-hour rate
  const globalRate = Math.round((globalCount / hourFraction) * 10) / 10;
  const previousRate = previousGlobalCount; // Previous window was complete (1 hour)

  // Per-entity metrics
  const perEntity = new Map<string, { rate: number; level: VelocityLevel; trend: VelocityTrend }>();

  for (const [entityKey, count] of entityCounts) {
    const rate = Math.round((count / hourFraction) * 10) / 10;
    const prevEntityCount = previousWindow?.entityCounts.get(entityKey) ?? 0;

    perEntity.set(entityKey, {
      rate,
      level: classifyLevel(rate),
      trend: classifyTrend(rate, prevEntityCount),
    });
  }

  return {
    global: {
      rate: globalRate,
      level: classifyLevel(globalRate),
      trend: classifyTrend(globalRate, previousRate),
    },
    perEntity,
    windowStart: new Date(currentWindowStart).toISOString(),
  };
}
